import React, { Fragment, useState, useEffect } from 'react'
import { Portal } from 'react-portal'
import track from 'react-tracking'

import BEMHelper from '@ynap/bem-helper'
import { Button, AsyncButton, AsyncState } from '@ynap/button'
import EmptyList from '@ynap/empty-list'
import { LineItemSkeleton } from '@ynap/line-item'
import { Tooltip } from '@ynap/notification'
import Overlay, { StopPropagation } from '@ynap/overlay'
import { QuickViewLayout } from '@ynap/product-details-page';
import Spinner from '@ynap/spinner'

import isNonNull from "app/utils/isNonNull"
import isString from "app/utils/isString"
import { myaccountPageData } from '../analytics'
import { Reservation, ReservationProps, Cart, pdpDetail } from './types'
import { ReservationItem } from "./ReservationsItem/ReservationItem"

const bem = new BEMHelper('Reservations')

function getReservationsFromCart(cart: Cart): Array<string> {
    if (!cart?.orderItem) {
        return []
    }
    return cart.orderItem.map((item) => item.xitem_reservationId).filter(isNonNull)
}

export function filterReservations(reservations: Array<Reservation>, alreadyInCart: Array<string>): Array<Reservation> {
    if (!alreadyInCart || alreadyInCart.length === 0) {
        return reservations
    }
    return reservations.filter((item) => !alreadyInCart.includes(item.reservationId))
}

const Reservations: React.FC<ReservationProps> = ({
    tracking,
    className,
    messages,
    cartState,
    reservations,
    fetchReservationsError,
    fetchReservations,
    fetchCart,
    addToCart,
    addMultipleToCart,
    addToCartResponse,
    config,
    locale,
}) => {
    const [reservationsInCart, setReservationsInCart] = useState<Array<string>>([])
    const [addAllLoading, setAddAllLoading] = useState(false)
    const [addAllError, setAddAllError] = useState<string | null>(null)
    const originalOverlayState = { show: false, inCart: false, details: undefined }
    const [itemOverlay, setItemOverlay] = useState<{ show: boolean, inCart: boolean, details?: pdpDetail }>(originalOverlayState)

    const loading = addToCartResponse ? addToCartResponse.loading : false
    const { links } = config.head
    const { fullPageHeader } = config?.head?.flags || {}

    useEffect(() => {
        tracking?.trackEvent?.(myaccountPageData({
            name: 'reservations',
            subCategory1: 'reservations'
        }))
        if (typeof fetchReservations == "function") {
            fetchReservations()
        }
        if (typeof fetchCart == "function") {
            fetchCart()
        }
    }, [])

    useEffect(() => {
        cartState?.payload && setReservationsInCart(getReservationsFromCart(cartState.payload))
    }, [cartState])

    function addAllToCart() {
        setAddAllLoading(true)

        const validReservations = buyableReservations ? filterReservations(buyableReservations, reservationsInCart) : []
        const itemsToAdd = validReservations.map(({ partNumber, quantity = 1, reservationId }) => ({
            partNumber,
            quantity,
            reservationId
        }))

        addMultipleToCart(itemsToAdd).then((response) => {
            if (response.type === 'ADD_MULTIPLE_TO_CART_SUCCESS') {
                const addedReservations = response.lastAddedItems.map((item) => item.reservationId)
                setReservationsInCart([...reservationsInCart, ...addedReservations])
                setAddAllError(null)
                setAddAllLoading(false)
            } else {
                setAddAllError(messages?.notifications?.errors?.({ error: response?.error?.errorKey }) ?? null)
                setAddAllLoading(false)
            }
        })
    }

    const buyableReservations = reservations?.filter(reservation => reservation?.details?.selectedProductVariant?.buyable?.canAddToBag)

    const allInBag =
        buyableReservations?.length == reservationsInCart.length &&
        buyableReservations.reduce((allIn, item) => {
            if (!allIn) return false
            return reservationsInCart.includes(item.reservationId)
        }, true)

    const asyncState: AsyncState = {
        loading: addAllLoading,
        primaryAction: {
            message: messages.reservations?.addAll?.(),
            click: addAllToCart,
            timeout: 2000
        },
        error: !addAllLoading && !!addAllError
    }

    const renderItemOverlay = () => {
        if (!itemOverlay.details) return null;
        const { details } = itemOverlay
        const { reservationId } = details
        const reset = () => setItemOverlay(originalOverlayState)
        const addItemToCart = (partNumber: string) =>
            addToCart(partNumber, { addToCartLoading: false, reservationId }).then((response) => {
                if (!response?.error) setReservationsInCart([...reservationsInCart, reservationId])
            })

        return (
            <Portal>
                <Overlay
                    type="pdp"
                    className={bem('overlay')}
                    onClose={reset}
                    handleBodyClick={reset}
                >
                    <StopPropagation>
                        <QuickViewLayout
                            {...details}
                            addToCart={!itemOverlay.inCart && addItemToCart}
                            messages={messages}
                            wishlistButton={null}
                            locale={locale.id}
                            addToCartLoading={loading}
                            errors={{
                                addToCart: {
                                    error: addToCartResponse?.error?.body?.error
                                }
                            }}
                        />
                    </StopPropagation>
                </Overlay>
            </Portal>
        )
    }

    const emptyReservation = fetchReservationsError || (reservations&&reservations.length < 1) ? 'emptyReservation' : ''
    const noReservationsUrl = config?.global?.urlConfig?.whatsnew?.construct?.replace('{{LOCALE}}', locale.id)

    return (
        <div className={bem(null, null, className)}>
            <div className={bem('info',null, emptyReservation)}>
                {!fullPageHeader && <h2 className={bem('title')}>{messages.reservations?.title?.()}</h2>}
                <div className={bem('message', null, emptyReservation)}>{messages.reservations?.message?.()}</div>
            </div>
            <div className={bem('line-items')}>
                <Spinner loading={loading}>
                    {fetchReservationsError ? (
                        <EmptyList className={bem('emptyState')} title={messages.reservations?.pageError?.()}>
                            <Button onClick={fetchReservations} type="primary-link" className={bem('emptyLink')}>
                                {messages.reservations?.retryButton?.()}
                            </Button>
                        </EmptyList>
                    ) : !reservations ? (
                        <Fragment>
                            <LineItemSkeleton />
                            <LineItemSkeleton />
                        </Fragment>
                    ) : (
                                <>
                                    {reservations.length > 0 && (
                                        <div className={bem('actions')}>
                                            <Tooltip
                                                type="error"
                                                message={addAllError ?? ""}
                                                defaultOpen={isString(addAllError)}
                                                open={isString(addAllError)}
                                            >
                                                {!allInBag ? (
                                                    <AsyncButton
                                                        className={bem('addAll')}
                                                        asyncState={asyncState}
                                                        type={'primary'}
                                                        disabled={!!addAllError}
                                                    />
                                                ) : (
                                                        <Button
                                                            className={bem('goToBag')}
                                                            onClick={() => {
                                                                location.href = links.checkout
                                                                    .replace('{language}', locale.language)
                                                                    .replace('{country}', locale.country)
                                                                    .toLowerCase()
                                                            }}
                                                        >
                                                            {messages.reservations?.goToBag?.()}
                                                        </Button>
                                                    )}
                                            </Tooltip>
                                        </div>
                                    )}
                                    <ul className={bem('items')}>
                                        {reservations.length > 0 ? (
                                            reservations.map((reservation: Reservation) =>
                                                <ReservationItem
                                                    key={reservation.reservationId}
                                                    reservation={reservation}
                                                    reservationsInCart={reservationsInCart}
                                                    setReservationsInCart={setReservationsInCart}
                                                    addToCart={addToCart}
                                                    addToCartResponse={addToCartResponse}
                                                    messages={messages}
                                                    onClick={() => setItemOverlay({
                                                        inCart: reservationsInCart.includes(reservation.reservationId),
                                                        show: true,
                                                        details: reservation.details
                                                    })}
                                                />
                                            )
                                        ) : (
                                                <EmptyList
                                                    className={bem('emptyState')}
                                                    title={messages?.reservations?.noReservations?.()}
                                                >
                                                    <Button
                                                        component="a"
                                                        href={noReservationsUrl}
                                                        type="primary-link"
                                                        className={bem('emptyLink')}
                                                    >
                                                        {messages?.reservations?.noReservationsLink?.()}
                                                    </Button>
                                                </EmptyList>
                                            )}
                                    </ul>
                                    {itemOverlay.show ? renderItemOverlay() : null}
                                </>
                            )}
                </Spinner>
            </div>
        </div>
    )
}

export default track({})(Reservations)